import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AutoAwesome,
  Psychology,
  Diamond,
  SelfImprovement,
  Star,
  Check,
  Phone,
  WhatsApp,
  TrendingUp,
  Security,
  Favorite,
  Groups,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { getOrganizationStructuredData, getLocalBusinessStructuredData } from '../data/structuredData';

const Home = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <TrendingUp />,
      title: t('home.experience'),
      description: t('home.experience'),
    },
    {
      icon: <Security />,
      title: t('home.authentic'),
      description: t('home.authentic'),
    },
    {
      icon: <Groups />,
      title: t('home.trusted'),
      description: t('home.trusted'),
    },
    {
      icon: <Favorite />,
      title: t('home.available'),
      description: t('home.available'),
    },
  ];

  const services = [
    {
      icon: <SelfImprovement />,
      title: t('services.pujaTitle'),
      description: t('services.pujas.ganeshPuja') + ', ' + t('services.pujas.durga') + ', ' + t('services.pujas.laxmi') + ' और अन्य',
      image: '/api/placeholder/300/200',
      items: [t('services.pujas.ganeshPuja'), t('services.pujas.durga'), t('services.pujas.laxmi'), t('services.pujas.saraswati')],
    },
    {
      icon: <Psychology />,
      title: t('services.astroTitle'),
      description: t('services.astrology.kundali') + ', ' + t('services.astrology.matching') + ', ' + t('services.astrology.career'),
      image: '/api/placeholder/300/200',
      items: [t('services.astrology.kundali'), t('services.astrology.matching'), t('services.astrology.career'), t('services.astrology.marriage')],
    },
    {
      icon: <AutoAwesome />,
      title: t('services.tantrikTitle'),
      description: t('services.tantrik.blackMagic') + ', ' + t('services.tantrik.negative') + ', ' + t('services.tantrik.protection'),
      image: '/api/placeholder/300/200',
      items: [t('services.tantrik.blackMagic'), t('services.tantrik.negative'), t('services.tantrik.protection'), t('services.tantrik.vashikaran')],
    },
    {
      icon: <Diamond />,
      title: t('services.gemstoneTitle'),
      description: t('services.gemstones.ruby') + ', ' + t('services.gemstones.pearl') + ', ' + t('services.gemstones.coral') + ' और अन्य',
      image: '/api/placeholder/300/200',
      items: [t('services.gemstones.ruby'), t('services.gemstones.pearl'), t('services.gemstones.coral'), t('services.gemstones.emerald')],
    },
  ];

  return (
    <Box>
      <SEOHead
        title="आचार्य अभिषेक पांडेय - प्रामाणिक ज्योतिष और तंत्र विशेषज्ञ | Delhi Astrologer"
        description="15+ वर्षों के अनुभव के साथ सभी प्रकार की पूजा, ज्योतिष कुंडली, तांत्रिक समाधान और रत्न उपचार की सेवा। जादू टोना से छुटकारा, वशीकरण और ग्रह शांति के उपाय। 24/7 उपलब्ध।"
        keywords="ज्योतिष, astrology, तंत्र, tantra, पूजा, puja, कुंडली, kundali, जादू टोना, black magic removal, रत्न, gemstones, वशीकरण, vashikaran, आचार्य अभिषेक पांडेय, Delhi astrologer"
        canonicalUrl="https://acharyaabhishekpandey.com"
        structuredData={[getOrganizationStructuredData(), getLocalBusinessStructuredData()]}
        language="hi"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #1976D2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40z'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  {t('home.title')}
                </Typography>
                
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 300,
                    mb: 3,
                    opacity: 0.9,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {t('home.subtitle')}
                </Typography>
                
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    lineHeight: 1.6,
                    opacity: 0.9,
                    maxWidth: 600,
                  }}
                >
                  {t('home.heroDescription')}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/services"
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {t('common.learnMore')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WhatsApp />}
                    href="https://wa.me/919838167840"
                    target="_blank"
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {t('common.contactNow')}
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: { xs: 200, md: 280 },
                    height: { xs: 200, md: 280 },
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    '& img': {
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                      width: '100%',
                      height: '100%',
                    },
                  }}
                  src="/images/acharya-abhishek-pandey.jpeg"
                  alt="आचार्य अभिषेक पांडेय - ज्योतिष विशेषज्ञ"
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} sx={{ color: '#FFD700', fontSize: 24 }} />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                  {t('home.trusted')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{ mb: 6, fontWeight: 600, color: 'text.primary' }}
        >
          {t('home.whyChooseTitle')}
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: 'primary.light',
                    color: 'white',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Overview Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 8 }}>
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600, color: 'text.primary' }}
          >
            {t('home.servicesTitle')}
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardMedia
                    sx={{
                      height: 140,
                      background: `linear-gradient(45deg, ${index % 2 === 0 ? '#FF6B35' : '#1976D2'} 30%, ${index % 2 === 0 ? '#FF8F65' : '#42A5F5'} 90%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ color: 'white', fontSize: 48 }}>
                      {service.icon}
                    </Box>
                  </CardMedia>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {service.title}
                    </Typography>
                    
                    <List dense>
                      {service.items.map((item, itemIndex) => (
                        <ListItem key={itemIndex} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2 }}>
                    <Button
                      size="small"
                      component={Link}
                      to="/services"
                      sx={{ fontWeight: 600 }}
                    >
                      {t('common.learnMore')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/services"
              sx={{ 
                px: 4, 
                py: 1.5,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              {t('common.viewAll')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Contact CTA Section */}
      <Box
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          py: 6,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t('contact.getInTouch')}
            </Typography>
            
            <Typography
              variant="h6"
              sx={{ mb: 4, opacity: 0.9 }}
            >
              {t('contact.description')}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Phone />}
                href="tel:+919838167840"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    border: '2px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {t('common.contactNow')}
              </Button>
              
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    border: '2px solid rgba(255,255,255,0.5)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {t('contact.form.title')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;